import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Button, Card, Chip, FormControl, FormLabel, Input, Stack, Typography, Sheet, Tabs, TabList, Tab } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import { DocumentWizardSharedProps } from './DocumentWizardShared';
import { MaterialsCard, LabourCard, EquipmentCard, AdditionalCostsCard, DurationCard } from './DocumentWizardCards';
import { DepositCard, TotalsCard } from './DocumentWizardTotals';
import { computeTotals } from '../types/documents.types';
import DescriptionAssistant from './DescriptionAssistant';
import { TITLE_TEMPLATES } from '../data/titleTemplates';
import QuoteVisualDesignerCard from './QuoteVisualDesignerCard';
import { VisualDesignerPairingCard, type VisualDesignerPairingModel } from './VisualDesignerPairingCard';
import { VersionPairingMap } from './VersionPairingMap';
import {
  buildVersionPairingRows,
  pairingLabelForVersion,
  type QuoteLikeVersion,
} from '../utils/versionPairings';
import type { DocumentShareLinkRecord } from '../services/documentShareLinks.service';
import { buildVersionTransmissionBadgeState } from '../utils/transmissionInfo';

interface EditWizardProps extends DocumentWizardSharedProps {
  documentId: string;
  issuedAt?: string | null;
  versions: Array<{
    id: string;
    versionNumber: number;
    isActive: boolean;
    isTransmitted: boolean;
    transmittedAt: string | null;
    overrideUnlocked: boolean;
    overrideUnlockedAt: string | null;
  }>;
  versionShareLinks: DocumentShareLinkRecord[];
  currentVersionId: string | null;
  isVersionDirty: boolean;
  handleCreate: () => Promise<void | boolean>;
  onOpenMaterial: () => void;
  onOpenEquipment: () => void;
  onOpenLabour: () => void;
  onOpenHelp: () => void;
  onVersionChange: (versionId: string) => Promise<void>;
  onAddVersion: () => Promise<void>;
  onActivateVersion: () => Promise<void>;
  onDeleteVersion: () => Promise<void>;
  onUnlockVersionOverride: (reason?: string) => Promise<void>;
  onRelockVersionOverride: () => Promise<void>;
  versionLayout?: 'horizontal' | 'vertical';
  onCancel?: () => void;
  omitVisualDesigner?: boolean;
  omitLockControls?: boolean;
  omitActionButtons?: boolean;
  hideVersions?: boolean;
  visualPairing?: VisualDesignerPairingModel;
}

export const EditWizard: React.FC<EditWizardProps> = ({
  kind,
  lifecycleRef,
  title,
  setTitle,
  description,
  setDescription,
  selectedCustomer,
  lines,
  setLines,
  materials,
  equipment,
  labourRates,
  orgs,
  organisationId,
  displayDays,
  setDisplayDays,
  displayHours,
  setDisplayHours,
  depositEnabled,
  setDepositEnabled,
  depositMode,
  setDepositMode,
  depositPct,
  setDepositPct,
  depositFixed,
  setDepositFixed,
  depositAmount,
   paymentStructureApplicable,
   setPaymentStructureApplicable,
   paymentStructurePattern,
   setPaymentStructurePattern,
  fees,
  setFees,
  vatEnabled,
  setVatEnabled,
  vatPct,
  setVatPct,
  documentId,
  issuedAt,
  versions,
  versionShareLinks,
  currentVersionId,
  isVersionDirty,
  handleCreate,
  loading,
  onOpenMaterial,
  onOpenEquipment,
  onOpenLabour,
  onOpenHelp,
  onVersionChange,
  onAddVersion,
  onActivateVersion,
  onDeleteVersion,
  onUnlockVersionOverride,
  onRelockVersionOverride,
  versionLayout = 'horizontal',
  onCancel,
  omitVisualDesigner = false,
  omitLockControls = false,
  omitActionButtons = false,
  hideVersions = false,
  visualPairing,
}) => {
  const navigate = useNavigate();

  const setTitleNormalized = (next: string) => {
    const s = String(next ?? '');
    if (kind === 'Estimate' || kind === 'Quotation') {
      if (!s) return setTitle(s);
      return setTitle(s.charAt(0).toLowerCase() + s.slice(1));
    }
    return setTitle(s);
  };
  
  const totals = useMemo(() => {
    const result = computeTotals(lines, fees, { enabled: vatEnabled, pct: vatPct });
    return result;
  }, [lines, fees, vatEnabled, vatPct]);

  const defaultVisualDesignerReference = useMemo(() => {
    const name = String(selectedCustomer?.name || '').trim();
    if (!name) return '';
    const parts = name.split(/\s+/).filter(Boolean);
    const surname = (parts.slice(1).join(' ') || parts[0] || '').trim();
    return surname.toUpperCase();
  }, [selectedCustomer?.name]);

  const versionBadgeState = useMemo(() => {
    const state = new Map<string, { hasSent: boolean; showOutdated: boolean; hasNullified: boolean }>();
    versions.forEach((v) => {
      state.set(v.id, buildVersionTransmissionBadgeState(v.id, versionShareLinks));
    });
    return state;
  }, [versions, versionShareLinks]);

  const currentVersion = useMemo(
    () => versions.find(v => v.id === currentVersionId) || null,
    [versions, currentVersionId]
  );
  const pairingRows = useMemo(() => {
    if ((kind !== 'Estimate' && kind !== 'Quotation') || !visualPairing) return [];
    const quoteVersions: QuoteLikeVersion[] = versions.map((version) => ({
      id: version.id,
      documentId,
      versionNumber: version.versionNumber,
      isActive: version.isActive,
      isTransmitted: version.isTransmitted,
      overrideUnlocked: version.overrideUnlocked,
      visualSurveyId: visualPairing.surveys.find((survey) => survey.documentVersionId === version.id)?.id || null,
      lifecycleRef: lifecycleRef || '',
      kind,
    }));
    return buildVersionPairingRows(visualPairing.surveys, quoteVersions);
  }, [kind, visualPairing, versions, documentId, lifecycleRef]);
  const isCurrentVersionLocked = !!(kind !== 'Invoice' && currentVersion?.isTransmitted && !currentVersion?.overrideUnlocked);
  const lockBodyRef = useRef<HTMLDivElement>(null);
  const lockLinesRef = useRef<HTMLDivElement>(null);
  const documentNoun = kind === 'Estimate' ? 'estimate' : 'quote';
  const versionsTitle = kind === 'Estimate' ? 'Estimate versions' : 'Quote versions';

  useEffect(() => {
    const nodes = [lockBodyRef.current, lockLinesRef.current] as Array<(HTMLDivElement & { inert?: boolean }) | null>;
    nodes.forEach((node) => {
      if (!node) return;
      node.inert = isCurrentVersionLocked;
      if (isCurrentVersionLocked) {
        const active = document.activeElement;
        if (active instanceof HTMLElement && node.contains(active)) active.blur();
      }
    });
  }, [isCurrentVersionLocked, currentVersionId]);

  const selectVersion = async (val: string) => {
    if (!val) return;
    if (val === 'new') {
      if (isVersionDirty) {
        const choice = window.confirm('You have unsaved changes to this version. Save them before creating a new version?\nOK = Save, Cancel = Discard');
        if (choice) {
          await handleCreate();
        }
      }
      await onAddVersion();
      return;
    }
    if (val !== currentVersionId) {
      if (isVersionDirty) {
        const choice = window.confirm('You have unsaved changes to this version. Save them before switching?\nOK = Save, Cancel = Discard');
        if (choice) {
          await handleCreate();
        }
      }
      await onVersionChange(val);
    }
  };

  const versionCaption = (v: EditWizardProps['versions'][number]) => {
    if (v.versionNumber === 1) return kind === 'Estimate' ? 'Original estimate' : 'Original quotation';
    if (v.isActive) return 'Current revision';
    return 'Earlier revision';
  };

  const versionBadges = (v: EditWizardProps['versions'][number]) => {
    const sent = !!versionBadgeState.get(v.id)?.hasSent;
    const draft = !v.isTransmitted && !sent;
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        {v.isActive && (
          <Chip size="sm" variant="solid" color="primary">CURRENT</Chip>
        )}
        {draft && (
          <Chip size="sm" variant="outlined" color="neutral">DRAFT</Chip>
        )}
        {v.id === currentVersionId && !v.isActive && versionLayout !== 'vertical' && (
          <Chip size="sm" variant="outlined" color="primary">Viewing</Chip>
        )}
        {v.isTransmitted && (
          <Chip size="sm" variant="soft" color={v.overrideUnlocked ? 'warning' : 'neutral'}>
            {v.overrideUnlocked ? 'OVERRIDE OPEN' : 'LOCKED'}
          </Chip>
        )}
        {sent && (
          <Chip size="sm" variant="soft" color="primary">SENT</Chip>
        )}
        {versionBadgeState.get(v.id)?.showOutdated && (
          <Chip size="sm" variant="soft" color="warning">OUTDATED QUOTE</Chip>
        )}
        {versionBadgeState.get(v.id)?.hasNullified && (
          <Chip size="sm" variant="soft" color="danger">NULLIFIED</Chip>
        )}
      </Stack>
    );
  };

  return (
    <>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2, gap: 2 }}>
        {versionLayout === 'vertical' ? (
          <Box>
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
              {kind === 'Estimate' ? 'Estimate' : kind === 'Quotation' ? 'Quotation' : 'Invoice'}
            </Typography>
            <Typography level="h3">{lifecycleRef || `Edit ${kind}`}</Typography>
          </Box>
        ) : (
          <Typography level="h3">Edit {kind === 'Estimate' ? 'Estimate' : kind === 'Quotation' ? 'Quotation' : 'Invoice'}{lifecycleRef ? ` - ${lifecycleRef}` : ''}</Typography>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button size="sm" variant="outlined" onClick={onOpenMaterial}>Manage Materials</Button>
          <Button size="sm" variant="outlined" onClick={onOpenEquipment}>Manage Equipment</Button>
          <Button size="sm" variant="outlined" onClick={onOpenLabour}>Manage Labour Rates</Button>
        </Stack>
      </Stack>

      {/* Versions (not applicable to invoices) */}
      {kind !== 'Invoice' && !hideVersions && (
      <Card sx={{ p: 2, mb: 2 }}>
        {versionLayout === 'vertical' ? (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} sx={{ mb: 1.5 }}>
              <Box>
                <Typography level="title-md">{versionsTitle}</Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  Select a version to view everything attached to it.
                </Typography>
              </Box>
              <Button size="sm" variant="outlined" color="primary" onClick={() => { void selectVersion('new'); }}>
                + Add version
              </Button>
            </Stack>
            {pairingRows.length > 0 && (
              <VersionPairingMap
                rows={pairingRows}
                currentVersionId={currentVersionId}
                currentSurveyId={visualPairing?.selectedSurveyId}
                onSelectSurvey={visualPairing?.onSelectSurvey}
                onSelectVersion={(versionId) => { void selectVersion(versionId); }}
              />
            )}
          </>
        ) : (
          <Typography level="title-md" sx={{ mb: 1 }}>Versions</Typography>
        )}
        <Stack spacing={1}>
          {versionLayout === 'vertical' ? (
            <Stack spacing={1}>
              {versions.map((v) => {
                const selected = v.id === currentVersionId;
                return (
                  <Box
                    key={v.id}
                    onClick={() => { void selectVersion(v.id); }}
                    sx={{
                      p: 1.5,
                      border: '2px solid',
                      borderColor: selected ? 'primary.400' : 'divider',
                      borderRadius: 'md',
                      cursor: 'pointer',
                      bgcolor: selected ? 'primary.50' : 'background.surface',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                      <Box>
                        <Typography level="title-sm">
                          Version {v.versionNumber}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          {versionCaption(v)}
                          {pairingLabelForVersion(v.id, pairingRows) ? ` · ${pairingLabelForVersion(v.id, pairingRows)}` : ''}
                        </Typography>
                      </Box>
                      {versionBadges(v)}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Tabs value={currentVersionId || (versions[0]?.id || null)} onChange={async (_, v) => {
              await selectVersion(v ? String(v) : '');
            }}>
              <TabList>
                {versions.map(v => (
                  <Tab key={v.id} value={v.id}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Typography level="body-sm">{`v${v.versionNumber}${v.isActive ? ' (active)' : ''}`}</Typography>
                      {v.isTransmitted && (
                        <Chip size="sm" variant="soft" color={v.overrideUnlocked ? 'warning' : 'neutral'}>
                          {v.overrideUnlocked ? 'OVERRIDE OPEN' : 'LOCKED'}
                        </Chip>
                      )}
                      {versionBadgeState.get(v.id)?.hasSent && (
                        <Chip size="sm" variant="soft" color="primary">SENT</Chip>
                      )}
                      {versionBadgeState.get(v.id)?.showOutdated && (
                        <Chip size="sm" variant="soft" color="warning">OUTDATED QUOTE</Chip>
                      )}
                      {versionBadgeState.get(v.id)?.hasNullified && (
                        <Chip size="sm" variant="soft" color="danger">NULLIFIED</Chip>
                      )}
                    </Stack>
                  </Tab>
                ))}
                <Tab value="new">{`+ Add`}</Tab>
              </TabList>
            </Tabs>
          )}
          {currentVersion?.isTransmitted && !omitLockControls && (
            <Card variant="soft" sx={{ p: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                <Typography level="body-sm">
                  {currentVersion.overrideUnlocked
                    ? 'Override mode is active for this transmitted version. Changes will update this historical version and its existing URL. Saving will relock automatically.'
                    : 'This transmitted version is immutable by default. Create a new version to amend, or explicitly unlock override.'}
                </Typography>
                {currentVersion.overrideUnlocked ? (
                  <Button size="sm" color="warning" variant="outlined" onClick={onRelockVersionOverride}>
                    Relock version
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    color="neutral"
                    variant="outlined"
                    onClick={async () => {
                      const reason = window.prompt('Reason for override unlock (optional):', '') || '';
                      await onUnlockVersionOverride(reason);
                    }}
                  >
                    Unlock override
                  </Button>
                )}
              </Stack>
            </Card>
          )}
          {versionLayout !== 'vertical' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
              {currentVersionId && !versions.find(v => v.id === currentVersionId)?.isActive && (
                <>
                  <Button size="sm" variant="solid" color="success" onClick={onActivateVersion}>Set Active</Button>
                  <Button size="sm" color="danger" variant="outlined" onClick={onDeleteVersion}>Delete</Button>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Card>
      )}

      {/* Survey workspace hides version tabs — keep unlock/relock available for small updates. */}
      {kind !== 'Invoice' && hideVersions && currentVersion?.isTransmitted && !omitLockControls && (
        <Card variant="soft" sx={{ p: 1.25, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <Chip size="sm" variant="soft" color={currentVersion.overrideUnlocked ? 'warning' : 'neutral'}>
                {currentVersion.overrideUnlocked ? 'OVERRIDE OPEN' : 'LOCKED'}
              </Chip>
              <Typography level="body-sm">
                {currentVersion.overrideUnlocked
                  ? 'Unlocked for small updates. Save will persist changes and relock.'
                  : 'Unlock override to make a small update to this survey sheet.'}
              </Typography>
            </Stack>
            {currentVersion.overrideUnlocked ? (
              <Button size="sm" color="warning" variant="outlined" onClick={onRelockVersionOverride}>
                Relock
              </Button>
            ) : (
              <Button
                size="sm"
                color="neutral"
                variant="outlined"
                onClick={async () => {
                  const reason = window.prompt('Reason for override unlock (optional):', '') || '';
                  await onUnlockVersionOverride(reason);
                }}
              >
                Unlock override
              </Button>
            )}
          </Stack>
        </Card>
      )}

      <Card sx={{ p: 2, mb: 2 }}>
        {kind !== 'Invoice' && versionLayout === 'vertical' && currentVersion && !hideVersions && (
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} sx={{ mb: 1.5 }} flexWrap="wrap">
            <Typography level="title-md">Visual Designer Link</Typography>
            {currentVersionId && !currentVersion.isActive && (
              <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                <Button size="sm" variant="solid" color="success" onClick={onActivateVersion}>Set Active</Button>
                <Button size="sm" color="danger" variant="outlined" onClick={onDeleteVersion}>Delete</Button>
              </Stack>
            )}
          </Stack>
        )}
        {visualPairing && kind !== 'Invoice' && !hideVersions && (
          <VisualDesignerPairingCard
            pairing={visualPairing}
            versionNumber={currentVersion?.versionNumber}
            documentNoun={documentNoun}
            hideHeading
            surveyHints={Object.fromEntries(
              pairingRows
                .filter((row) => row.surveyId)
                .map((row) => [row.surveyId as string, row.versionShortLabel || 'unpaired'])
            )}
          />
        )}
        {kind === 'Invoice' && (
          <Typography level="title-md" sx={{ mb: 1 }}>Details</Typography>
        )}
        {kind !== 'Invoice' && versionLayout !== 'vertical' && (
          <Typography level="title-md" sx={{ mb: 1 }}>Details</Typography>
        )}
        {kind !== 'Invoice' && versionLayout === 'vertical' && (
          <Typography level="title-sm" sx={{ mb: 1 }}>Details</Typography>
        )}
        <Box
          ref={lockBodyRef}
          sx={isCurrentVersionLocked ? { opacity: 0.55, pointerEvents: 'none', filter: 'grayscale(0.18)' } : undefined}
        >
        <Stack spacing={1}>
          <FormControl>
            <FormLabel>Title</FormLabel>
            <Input value={title} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitleNormalized(e.target.value)} />
          </FormControl>
          {/* Document Date (PDF Generation Date) - Read-only display */}
          {issuedAt && (
            <FormControl>
              <FormLabel>Document Date</FormLabel>
              <Typography level="body-sm" sx={{ 
                p: 1, 
                borderRadius: 'sm', 
                bgcolor: 'neutral.50',
                border: '1px solid',
                borderColor: 'neutral.200'
              }}>
                {new Date(issuedAt).toLocaleDateString('en-GB', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </Typography>
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                This is the date selected when generating the PDF. You can change it when exporting.
              </Typography>
            </FormControl>
          )}
          {/* Title Templates */}
          <Sheet variant="soft" sx={{ p: 1, borderRadius: 6 }}>
            <Typography level="title-sm" sx={{ mb: 1 }}>Title Templates</Typography>
            <Stack spacing={1}>
              {TITLE_TEMPLATES.filter(t => {
                const code = orgs.find(o => o.id === organisationId)?.code;
                return !t.org || t.org === code;
              }).map((t, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <Typography level="body-sm" sx={{ flex: 1 }}>{t.text}</Typography>
                  <Button size="sm" onClick={() => setTitleNormalized(t.text)}>Use</Button>
                </Stack>
              ))}
            </Stack>
          </Sheet>
          <DescriptionAssistant
            orgName={orgs.find(o => o.id === organisationId)?.name}
            orgCode={orgs.find(o => o.id === organisationId)?.code}
            reference={lifecycleRef}
            title={title}
            value={description}
            lines={lines}
            onChange={setDescription}
          />
        </Stack>
        </Box>
      </Card>

      <Box
        ref={lockLinesRef}
        sx={isCurrentVersionLocked ? { opacity: 0.55, pointerEvents: 'none', filter: 'grayscale(0.18)' } : undefined}
      >
      {/* Quote Mode Visual Designer (versioned on line JSON) */}
      {(!omitVisualDesigner && kind !== 'Invoice') && (
        <QuoteVisualDesignerCard
          lines={lines}
          setLines={setLines}
          defaultReference={defaultVisualDesignerReference}
          documentId={documentId}
          customerId={selectedCustomer?.id ?? null}
          currentVersionId={currentVersionId}
          availableVersions={versions}
        />
      )}

      {/* Line Items */}
      <MaterialsCard lines={lines} setLines={setLines} materials={materials} />
      <LabourCard lines={lines} setLines={setLines} labourRates={labourRates} />
      <EquipmentCard lines={lines} setLines={setLines} equipment={equipment} />
      <AdditionalCostsCard lines={lines} setLines={setLines} />

      {/* Duration */}
      <DurationCard 
        displayDays={displayDays} 
        setDisplayDays={setDisplayDays} 
        displayHours={displayHours} 
        setDisplayHours={setDisplayHours} 
      />

      {/* Deposit */}
      <DepositCard 
        depositEnabled={depositEnabled}
        setDepositEnabled={setDepositEnabled}
        depositMode={depositMode}
        setDepositMode={setDepositMode}
        depositPct={depositPct}
        setDepositPct={setDepositPct}
        depositFixed={depositFixed}
        setDepositFixed={setDepositFixed}
        depositAmount={depositAmount}
        paymentStructureApplicable={paymentStructureApplicable}
        setPaymentStructureApplicable={setPaymentStructureApplicable}
        paymentStructurePattern={paymentStructurePattern}
        setPaymentStructurePattern={setPaymentStructurePattern}
        grandTotal={totals.grandTotal}
      />

      {/* Totals */}
      <TotalsCard 
        fees={fees}
        setFees={setFees}
        vatEnabled={vatEnabled}
        setVatEnabled={setVatEnabled}
        vatPct={vatPct}
        setVatPct={setVatPct}
        displayDays={displayDays}
        displayHours={displayHours}
        totals={totals}
      />
      </Box>

      {/* Action Buttons stay clickable so Cancel still works on a locked version */}
      {!omitActionButtons && (
      <Card sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="soft" onClick={onOpenHelp}>Help?</Button>
          <Button 
            variant="outlined" 
            onClick={() => (onCancel ? onCancel() : navigate(`/docs/${documentId}`))}
          >
            Cancel
          </Button>
          <Button onClick={handleCreate} loading={loading} disabled={isCurrentVersionLocked}>
            {isCurrentVersionLocked ? 'Version Locked' : 'Save Changes'}
          </Button>
        </Stack>
      </Card>
      )}
    </>
  );
};
